import { type ReactElement, cloneElement, isValidElement, useId } from "react";

import { cn } from "@/lib/utils";

/* FormField — guide §5: every input wraps in this so labels, helpers,
   errors, and required asterisks are consistent across the app. The child
   input gets `id`, `aria-describedby`, and `aria-invalid` automatically. */

export interface FormFieldProps {
  label: React.ReactNode;
  htmlFor?: string;
  helperText?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  className?: string;
  children: ReactElement<{
    id?: string;
    "aria-describedby"?: string;
    "aria-invalid"?: boolean | "true" | "false";
    className?: string;
  }>;
}

export function FormField({
  label,
  htmlFor,
  helperText,
  error,
  required,
  className,
  children,
}: FormFieldProps) {
  const generatedId = useId();
  const inputId = htmlFor ?? children.props.id ?? generatedId;
  const helpId = `${inputId}-help`;
  const errId = `${inputId}-err`;

  const describedBy = error ? errId : helperText ? helpId : undefined;

  const child = isValidElement(children)
    ? cloneElement(children, {
        id: inputId,
        "aria-describedby": describedBy,
        "aria-invalid": error ? "true" : undefined,
        className: cn(children.props.className, error && "border-destructive"),
      })
    : children;

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label
        htmlFor={inputId}
        className="text-xs font-semibold text-foreground"
      >
        {label}
        {required && (
          <span className="ml-0.5 text-destructive" aria-hidden="true">
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>

      {child}

      {error ? (
        <p id={errId} className="text-xs text-[#B91C1C]" role="alert">
          {error}
        </p>
      ) : helperText ? (
        <p id={helpId} className="text-xs text-slate">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
